import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-links-list-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionTypeLinksList} from '../doppe-action-type-links-list';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {asArray, evaluateFunction, iterableMapToArray, ObservableValue} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeCommonListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-common-list-view-item/doppe-action-type-common-list-view-item';
import {
    doppeActionLayoutMediaRatioResolveWidthHeightRatio,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueToggleHidden,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemCalculateItemSections,
    doppeListActionSettingsFilterHiddenListItemsFromActionView
} from '../../../client-server-common';
import {useBymoPageContextProps} from '../../../doppe-sdk';
import {
    doppeHideableValueGetValue,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeLinkWidgetInner
} from '../../doppe-action-type-link-view/doppe-action-type-link-view-widget/doppe-action-type-link-view-widget';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    doppeViewerActionHandleListItemClick
} from '../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {
    useAssertWidgetRenderingContext
} from '../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';

export const DoppeActionTypeLinksListWidget = React.memo(function DoppeActionTypeLinksListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeLinksList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        observableCurrentItemIdToRender
    } = useAssertWidgetRenderingContext();

    const actualObservableCurrentItemIdToRender = evaluateFunction(() => {
        return observableCurrentItemIdToRender ? observableCurrentItemIdToRender : new ObservableValue<string | null>(null)
    })

    const actionSettings = actionContext.action.settings;

    const {
        listDataItems,
        layout,
        mediaRatio,
        mediaSize,

        previewShowCoupon,
        previewShowDescription,
        previewShowTitle,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
    } = actionSettings;

    const expandedLinkId = useObservableValue(actualObservableCurrentItemIdToRender);

    const currentLinkToShow = useMemo(
        () => listDataItems.find(link => link.id === expandedLinkId) ?? null,
        [listDataItems, expandedLinkId]);

    const bymoPageContextProps = useBymoPageContextProps();

    const availableItems = useMemo(() => {
        return iterableMapToArray(doppeListActionSettingsFilterHiddenListItemsFromActionView(listDataItems), (listItem) => {
            return {
                ...listItem,
                businessEntityDto: undefined
            }
        })
    }, [listDataItems, bymoPageContextProps]);

    const listViewDataSource = useMemo(() => {
        return listViewCreateDataSource(availableItems, {
            idFunction: item => item.id
        });
    }, [availableItems]);

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            {evaluateFunction(() => {
                if (!currentLinkToShow) {
                    return (
                        <DoppeActionTypeWidgetItemList
                            className={style(classes.listContent)}
                            layout={layout}
                            mediaSize={mediaSize}
                            listTitle={actionSettings.mainTitle}
                            media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                            mediaViewSettings={actionSettings.mainMediaViewSettings}
                            mediaViewLayout={actionSettings.mainMediaViewLayout}
                            onItemClick={eventData => {
                                doppeViewerActionHandleListItemClick(actionContext, {
                                    itemId: eventData.id,
                                    itemIndex: availableItems.findIndex(item => item.id === eventData.id),
                                    itemLink: eventData.url,
                                    couponCode: eventData.couponCode,
                                })
                            }}
                            actionContext={actionContext}
                            renderItem={(dataItem, layout, index) => {
                                const {
                                    image,
                                    title,
                                    description,
                                    ribbon,
                                } = dataItem;

                                return (
                                    <DoppeActionTypeCommonListViewItem
                                        mediaSize={mediaSize}
                                        actionContext={actionContext}
                                        innerItemId={dataItem.id}
                                        innerItemIndex={index}
                                        title={title}
                                        description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                        image={image ?? null}
                                        layout={layout}
                                        gridImageRatio={doppeActionLayoutMediaRatioResolveWidthHeightRatio(mediaRatio)}
                                        isOneItem={listViewDataSource.length === 1}
                                        ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? doppeHideableValueGetValue(ribbon) : undefined}
                                        hideImageWhenPossible={!itemShowImage}
                                        hideTitleWhenPossible={!itemShowTitle}
                                        hideDescriptionWhenPossible={!itemShowDescription}
                                    />
                                )
                            }}
                            dataSourceItems={listViewDataSource}
                        />
                    )
                } else {
                    return (
                        <DoppeActionTypeLinkWidgetInner
                            actionContext={actionContext}
                            reportGoalAchieved={(goalType) => {
                                actionContext.reportGoalAchieved({
                                    goalType: goalType,
                                    innerItemId: currentLinkToShow.id,
                                    innerItemPosition: listDataItems.findIndex(item => item.id === expandedLinkId)
                                });
                            }}
                            actionId={actionContext.action.id}
                            actionSettings={{
                                image: currentLinkToShow.image ? asArray(currentLinkToShow.image) : null,
                                couponCode: doppeHideableValueToggleHidden(
                                    currentLinkToShow.couponCode,
                                    isHidden => isHidden ? true : !previewShowCoupon
                                ),
                                description: doppeHideableValueToggleHidden(
                                    currentLinkToShow.description,
                                    isHidden => isHidden ? true : !previewShowDescription
                                ),
                                title: doppeHideableValueToggleHidden(
                                    currentLinkToShow.title,
                                    isHidden => isHidden ? true : !previewShowTitle
                                ),
                                url: currentLinkToShow.url,
                                ribbon: currentLinkToShow.ribbon,
                                ...doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, currentLinkToShow),
                                ...doppeListActionListItemCalculateItemSections(actionSettings.listItemTemplateSectionsSettings, currentLinkToShow),
                            }}
                            listItemId={currentLinkToShow.id}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


